import { useState } from 'react'

// ── helpers ────────────────────────────────────────────────────────────────────
const fmtN = n => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})
const num  = s => parseFloat(String(s).replace(/,/g,''))||0

const C = {
  deepBlue:'0A2342', midBlue:'1A4A7A', teal:'0D7377', accent:'14A8A8',
  lightTeal:'D0EAEA', white:'FFFFFF', offWhite:'F4F8F8', gold:'D4AC0D',
  red:'C0392B', lightGray:'E8EDEF', darkText:'1A2A3A', midGray:'64748B',
}

// ── HTML Report generator ────────────────────────────────────────────────────
function generateReport(form) {
  const budget   = num(form.budget)
  const forecast = num(form.forecast)
  const variance = forecast - budget
  const varPct   = budget > 0 ? ((variance / budget) * 100).toFixed(1) : '0'
  const over     = variance > 0
  const suppliers = form.suppliers.filter(s => s.name && s.value)
  const reasons   = form.varianceReasons.filter(r => r.trim())
  const today     = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})
  const minSupVal = suppliers.length ? Math.min(...suppliers.map(s=>num(s.value))) : 0

  const suppliersPage = suppliers.length > 0 ? `
  <div class="page">
    <div class="pg-header">SUPPLIER QUOTATIONS, AWARD &amp; FORECAST</div>
    <div class="pg-body">
      <div class="two-col">
        <div class="col-left">
          <div class="section-lbl">COMPLIANT QUOTATIONS RECEIVED</div>
          <table class="sup-tbl">
            <thead><tr><th>Supplier</th><th>Quoted (SAR)</th><th>Status</th></tr></thead>
            <tbody>
              ${suppliers.map(s => {
                const v = num(s.value)
                return `<tr class="${s.selected ? 'sup-sel' : ''}">
                  <td><strong>${s.name}</strong></td>
                  <td class="num">${fmtN(v)}</td>
                  <td class="${s.selected ? 'status-sel' : 'status-ns'}">${s.selected ? '✓ SELECTED' : 'Not Selected'}</td>
                </tr>`
              }).join('')}
            </tbody>
          </table>
          ${suppliers.find(s=>s.selected)?.reason ? `<div class="note-italic">${suppliers.find(s=>s.selected).reason}</div>` : ''}
        </div>
        <div class="col-right">
          <div class="section-lbl teal-bg">FORECAST BREAKDOWN</div>
          <div class="forecast-items">
            ${suppliers.find(s=>s.selected) ? `
            <div class="fc-item">
              <div class="fc-item-title">Selected Contract</div>
              <div class="fc-item-sub">${suppliers.find(s=>s.selected).name}</div>
              <div class="fc-item-val">SAR ${fmtN(num(suppliers.find(s=>s.selected).value))}</div>
            </div>` : ''}
            ${form.budgetNotes ? `<div class="fc-item"><div class="fc-item-title">Budget Context</div><div class="fc-item-sub">${form.budgetNotes}</div></div>` : ''}
          </div>
        </div>
      </div>
      <div class="total-strip">
        <div class="ts-left">
          <div class="ts-lbl">TOTAL FORECAST</div>
          <div class="ts-val">SAR ${fmtN(forecast)}</div>
        </div>
        <div class="ts-right">
          Budget <strong>${fmtN(budget)}</strong> &nbsp;|&nbsp; Forecast <strong>${fmtN(forecast)}</strong> &nbsp;|&nbsp; Variance <strong class="${over ? 'red' : 'teal'}">(${over?'+':''}${fmtN(variance)})</strong>
        </div>
      </div>
    </div>
    ${reasons.length > 0 ? `<div class="pg-footer-note">
      ${reasons.slice(0,2).map(r=>`<em>${r}</em>`).join(' — ')}
    </div>` : ''}
  </div>` : ''

  const conclusionPage = (form.conclusion || reasons.length > 0) ? `
  <div class="page page-dark">
    <div class="pg-header">COST VARIANCE &amp; CONCLUSION</div>
    <div class="pg-body">
      <div class="var-banner ${over ? 'var-red' : 'var-green'}">
        <div class="vb-left">
          <div class="vb-lbl">${over ? 'COST OVERRUN' : 'UNDER BUDGET'}</div>
          <div class="vb-val">${over?'+':''}SAR ${fmtN(Math.abs(variance))}</div>
          <div class="vb-sub">${over?'+':''}${varPct}% vs. Original Budget</div>
        </div>
        <div class="vb-right">
          <span class="vb-item">Budget: <strong>SAR ${fmtN(budget)}</strong></span>
          <span class="vb-arrow">→</span>
          <span class="vb-item">Forecast: <strong>SAR ${fmtN(forecast)}</strong></span>
        </div>
      </div>
      ${reasons.length > 0 ? `
      <div class="reasons-block">
        <div class="section-lbl">KEY VARIANCE DRIVERS</div>
        <ul class="reasons-list">
          ${reasons.map(r=>`<li>${r}</li>`).join('')}
        </ul>
      </div>` : ''}
      ${form.conclusion ? `<div class="conclusion-text">${form.conclusion}</div>` : ''}
    </div>
  </div>` : ''

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Cost Assessment – ${form.packageName}</title>
<style>
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family:'Segoe UI',Calibri,sans-serif; background:#9AA5B4; padding:20px; color:#1A2A3A; }
.toolbar { background:#0F2040; padding:12px 24px; display:flex; align-items:center; gap:12px; margin-bottom:20px; border-radius:8px; }
.tbtn { padding:8px 20px; border:none; border-radius:6px; cursor:pointer; font-size:13px; font-weight:700; }
.tbtn-p { background:#0D7377; color:#fff; }
.tbtn-g { background:transparent; border:1px solid rgba(255,255,255,.3); color:#fff; }
.toolbar-note { margin-left:auto; font-size:11px; color:rgba(255,255,255,.4); }

/* PAGES */
.page { width:280mm; background:#fff; margin:0 auto 24px; box-shadow:0 8px 40px rgba(0,0,0,.2); page-break-after:always; }
.page-dark { background:#0F2040; }

/* COVER */
.cover { background:#0F2040; min-height:170mm; display:flex; flex-direction:column; }
.cover-body { flex:1; padding:50px 56px 32px; }
.cover-tag { font-size:11px; letter-spacing:3px; color:#5EADB5; text-transform:uppercase; margin-bottom:14px; }
.cover-title { font-size:52px; font-weight:900; color:#fff; letter-spacing:2px; text-transform:uppercase; line-height:1.05; margin-bottom:16px; }
.cover-pkg { font-size:22px; color:#5EADB5; margin-bottom:12px; }
.cover-line { height:3px; width:100px; background:#5EADB5; margin-bottom:20px; }
.cover-proj { font-size:14px; font-weight:700; letter-spacing:3px; color:#fff; text-transform:uppercase; margin-bottom:40px; }
.cover-footer { background:linear-gradient(90deg,#1A5276,#0D7377); padding:20px 56px; display:flex; justify-content:space-between; align-items:center; }
.cf-lbl { font-size:12px; color:rgba(255,255,255,.7); }
.cf-val { font-size:13px; font-weight:700; color:#fff; }

/* CONTENT PAGES */
.pg-header { background:#0F2040; padding:16px 32px; font-size:20px; font-weight:900; color:#fff; letter-spacing:1px; }
.pg-body { padding:24px 32px; }
.pg-footer-note { background:#F8F9FA; padding:12px 32px; font-size:11px; color:#5D7080; border-top:1px solid #E0E6ED; font-style:italic; }

/* KPI STRIP */
.kpi-strip { display:grid; grid-template-columns:1fr 2fr; gap:16px; margin-bottom:20px; }
.kpi-main { background:#0F2040; padding:20px 24px; border-left:5px solid #5EADB5; }
.kpi-main-lbl { font-size:10px; font-weight:700; letter-spacing:2px; color:#5EADB5; text-transform:uppercase; margin-bottom:6px; }
.kpi-main-val { font-size:36px; font-weight:900; color:#F0C040; }
.kpi-side { background:#F4F8FA; padding:20px 24px; display:flex; align-items:center; gap:32px; }
.kpi-s-item { }
.kpi-s-lbl { font-size:10px; color:#5D7080; text-transform:uppercase; letter-spacing:1px; margin-bottom:4px; }
.kpi-s-val { font-size:18px; font-weight:700; color:#0F2040; }
.kpi-s-val.gold { color:#D4AC0D; }

/* TABLE */
.section-lbl { font-size:10px; font-weight:700; letter-spacing:2px; color:#0F2040; text-transform:uppercase; margin-bottom:10px; padding-bottom:6px; border-bottom:2px solid #0F2040; }
.section-lbl.teal-bg { background:#0D7377; color:#fff; padding:8px 12px; border:none; margin:0 0 0 0; }
.data-tbl { width:100%; border-collapse:collapse; font-size:12px; margin-bottom:16px; }
.data-tbl thead tr { background:#0F2040; }
.data-tbl th { padding:10px 14px; color:#fff; font-weight:700; font-size:11px; text-align:left; }
.data-tbl th:last-child { text-align:right; }
.data-tbl td { padding:10px 14px; border-bottom:1px solid #E8EDF3; }
.data-tbl td.num { text-align:right; font-weight:600; color:#D4AC0D; }
.data-tbl tr.total-row td { background:#F0F4F8; font-weight:900; font-size:13px; }
.data-tbl tr.total-row td.num { color:#F0C040; font-size:14px; }
.data-tbl tr.vo-row td { color:#D4AC0D; font-style:italic; }
.tbl-footer-note { font-size:11px; color:#5D7080; font-style:italic; margin-top:8px; }

/* SUPPLIERS */
.two-col { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:16px; }
.sup-tbl { width:100%; border-collapse:collapse; font-size:12px; }
.sup-tbl thead tr { background:#0F2040; }
.sup-tbl th { padding:10px 14px; color:#fff; font-weight:700; font-size:11px; text-align:left; }
.sup-tbl td { padding:10px 14px; border-bottom:1px solid #E8EDF3; }
.sup-tbl tr.sup-sel td { background:#E6F5F3; }
.status-sel { color:#0D7377; font-weight:700; }
.status-ns { color:#8FA3B1; }
.forecast-items { padding:12px; }
.fc-item { padding:12px 0; border-bottom:1px solid #E8EDF3; }
.fc-item:last-child { border:none; }
.fc-item-title { font-size:11px; font-weight:700; color:#0F2040; }
.fc-item-sub { font-size:10px; color:#8FA3B1; font-style:italic; margin:2px 0; }
.fc-item-val { font-size:20px; font-weight:900; color:#F0C040; }
.total-strip { background:#0F2040; padding:16px 24px; display:flex; align-items:center; justify-content:space-between; }
.ts-lbl { font-size:10px; color:rgba(255,255,255,.6); text-transform:uppercase; letter-spacing:1px; margin-bottom:2px; }
.ts-val { font-size:28px; font-weight:900; color:#F0C040; }
.ts-right { font-size:13px; color:rgba(255,255,255,.75); }
.ts-right strong { color:#fff; }
.note-italic { font-size:11px; color:#5D7080; font-style:italic; margin-top:10px; }

/* VARIANCE */
.var-banner { display:flex; align-items:center; justify-content:space-between; padding:20px 24px; margin-bottom:20px; }
.var-red { background:#C0392B; }
.var-green { background:#0D7377; }
.vb-lbl { font-size:10px; font-weight:700; letter-spacing:2px; color:rgba(255,255,255,.7); text-transform:uppercase; margin-bottom:4px; }
.vb-val { font-size:32px; font-weight:900; color:#fff; }
.vb-sub { font-size:12px; color:rgba(255,255,255,.7); margin-top:2px; }
.vb-right { font-size:15px; color:rgba(255,255,255,.85); display:flex; align-items:center; gap:12px; }
.vb-arrow { color:rgba(255,255,255,.5); font-size:20px; }
.reasons-block { margin-bottom:20px; }
.reasons-list { list-style:none; margin-top:10px; }
.reasons-list li { padding:10px 0; border-bottom:1px solid #E8EDF3; font-size:13px; color:#1A2A3A; padding-left:16px; position:relative; }
.reasons-list li:before { content:''; position:absolute; left:0; top:50%; transform:translateY(-50%); width:6px; height:6px; background:#0D7377; border-radius:1px; }
.conclusion-text { background:#F4F8FA; padding:16px 20px; border-left:4px solid #0D7377; font-size:13px; color:#1A2A3A; line-height:1.7; border-radius:0 8px 8px 0; }
.red { color:#E74C3C; }
.teal { color:#0D7377; }

@media print {
  * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; color-adjust:exact !important; }
  body { background:#fff; padding:0; }
  .toolbar { display:none; }
  .page { margin:0; box-shadow:none; }
}
</style>
</head>
<body>

<div class="toolbar">
  <button class="tbtn tbtn-p" onclick="window.print()">🖨 Print / Save PDF</button>
  <button class="tbtn tbtn-g" onclick="window.close()">← Back</button>
  <div class="toolbar-note">Cost Assessment · ${form.packageName} · Confidential · ${today}</div>
</div>

<!-- COVER -->
<div class="page">
  <div class="cover">
    <div class="cover-body">
      <div class="cover-tag">Cost Assessment</div>
      <div class="cover-title">COST<br>ASSESSMENT</div>
      <div class="cover-pkg">${form.packageName}</div>
      <div class="cover-line"></div>
      <div class="cover-proj">${form.projectName}</div>
    </div>
    <div class="cover-footer">
      <div>
        <div class="cf-lbl">Prepared for:</div>
        <div class="cf-val">Top Management Review</div>
      </div>
      <div>
        <div class="cf-lbl">Confidential</div>
        <div class="cf-val">${form.reportDate || today}</div>
      </div>
    </div>
  </div>
</div>

<!-- BUDGET PAGE -->
<div class="page">
  <div class="pg-header">ORIGINAL BUDGET</div>
  <div class="pg-body">
    <div class="kpi-strip">
      <div class="kpi-main">
        <div class="kpi-main-lbl">TOTAL ORIGINAL BUDGET</div>
        <div class="kpi-main-val">SAR ${fmtN(budget)}</div>
      </div>
      <div class="kpi-side">
        <div class="kpi-s-item">
          <div class="kpi-s-lbl">Forecast</div>
          <div class="kpi-s-val gold">SAR ${fmtN(forecast)}</div>
        </div>
        <div class="kpi-s-item">
          <div class="kpi-s-lbl">Variance</div>
          <div class="kpi-s-val" style="color:${over?'#C0392B':'#0D7377'}">${over?'+':''}SAR ${fmtN(variance)} (${over?'+':''}${varPct}%)</div>
        </div>
      </div>
    </div>
    ${form.budgetNotes ? `
    <div class="section-lbl" style="margin-bottom:12px">BUDGET CONTEXT</div>
    <div class="conclusion-text" style="margin-bottom:20px">${form.budgetNotes}</div>` : ''}
  </div>
  ${reasons.length > 0 ? `<div class="pg-footer-note">${reasons[0]}</div>` : ''}
</div>

${suppliersPage}
${conclusionPage}

</body></html>`

  const w = window.open('','_blank')
  w.document.write(html)
  w.document.close()
}


// ── COMPONENT ──────────────────────────────────────────────────────────────────
const EMPTY_SUPPLIER = () => ({ name:'', value:'', selected:false, reason:'' })

export default function CostAssessment() {
  const [form, setForm] = useState({
    projectName:     'Jeddah Oceanarium Project',
    packageName:     '',
    reportDate:      '',
    budget:          '',
    forecast:        '',
    budgetNotes:     '',
    suppliers:       [EMPTY_SUPPLIER(), EMPTY_SUPPLIER(), EMPTY_SUPPLIER()],
    varianceReasons: ['','','',''],
    conclusion:      '',
  })

  const [saved,    setSaved]    = useState([])
  const [toast,    setToast]    = useState(null)
  const [section,  setSection]  = useState(1)   // 1=basic 2=suppliers 3=variance 4=preview

  const h = (k,v) => setForm(f=>({...f,[k]:v}))

  const budget   = num(form.budget)
  const forecast = num(form.forecast)
  const variance = forecast - budget
  const varPct   = budget>0 ? ((variance/budget)*100).toFixed(1) : '0'
  const over     = variance > 0

  const showToast = (msg,type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3000) }

  const handleGenerate = () => {
    if (!form.packageName) return showToast('Package name is required','error')
    if (!form.budget)      return showToast('Budget is required','error')
    if (!form.forecast)    return showToast('Forecast is required','error')
    try {
      generateReport(form)
      showToast('Report opened in new tab — Print to PDF ✓')
      setSaved(s=>[{...form, id:Date.now()}, ...s])
    } catch(e) {
      showToast('Error generating PPTX: '+e.message,'error')
    }
  }

  const inputCls = "w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
  const labelCls = "block text-xs font-semibold text-[#6B7280] mb-1"

  const steps = [
    {n:1, label:'Package Info'},
    {n:2, label:'Suppliers'},
    {n:3, label:'Variance'},
    {n:4, label:'Preview'},
  ]

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>

      {/* Header */}
      <div className="bg-slate-900 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white font-bold text-lg">Cost Assessment Report</h1>
            <p className="text-[#9CA3AF] text-xs mt-0.5">Generate executive PPTX presentation for top management</p>
          </div>
          <button onClick={handleGenerate}
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold rounded-lg transition-colors">
            📑 Generate Report
          </button>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-6 flex gap-0">
          {steps.map(s=>(
            <button key={s.n} onClick={()=>setSection(s.n)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                section===s.n
                  ? 'border-indigo-600 text-[#1A7A4A]'
                  : 'border-transparent text-[#6B7280] hover:text-[#2E2C29]'
              }`}>
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs mr-2 ${
                section===s.n?'bg-[#1C1A18] text-white':'bg-slate-200 text-[#6B7280]'
              }`}>{s.n}</span>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">

        {/* STEP 1 — Package Info */}
        {section===1 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4">
            <h2 className="text-sm font-bold text-[#2E2C29] mb-4">Package Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Project Name</label>
                <input value={form.projectName} onChange={e=>h('projectName',e.target.value)} className={inputCls}/>
              </div>
              <div>
                <label className={labelCls}>Package / Scope Name *</label>
                <input value={form.packageName} onChange={e=>h('packageName',e.target.value)}
                  placeholder="e.g. Facade Aluminum & Glazing Works" className={inputCls}/>
              </div>
              <div>
                <label className={labelCls}>Report Date</label>
                <input value={form.reportDate} onChange={e=>h('reportDate',e.target.value)}
                  placeholder="e.g. March 2026" className={inputCls}/>
              </div>
              <div/>
              <div>
                <label className={labelCls}>Original Budget (SAR) *</label>
                <input value={form.budget} onChange={e=>h('budget',e.target.value)}
                  placeholder="0" className={inputCls}/>
              </div>
              <div>
                <label className={labelCls}>Forecast / Contract Value (SAR) *</label>
                <input value={form.forecast} onChange={e=>h('forecast',e.target.value)}
                  placeholder="0" className={inputCls}/>
              </div>
            </div>

            {/* Live variance */}
            {budget>0 && forecast>0 && (
              <div className={`rounded-xl p-4 flex items-center justify-between ${over?'bg-red-50 border border-red-200':'bg-emerald-50 border border-emerald-200'}`}>
                <div>
                  <p className="text-xs font-semibold text-[#6B7280]">Variance</p>
                  <p className={`text-xl font-bold ${over?'text-red-700':'text-emerald-700'}`}>
                    {over?'+':''}SAR {fmtN(variance)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#6B7280]">{over?'Over Budget ⚠':'Under Budget ✓'}</p>
                  <p className={`text-2xl font-bold ${over?'text-red-600':'text-emerald-600'}`}>{over?'+':''}{varPct}%</p>
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Budget Context / Notes (optional)</label>
              <textarea value={form.budgetNotes} onChange={e=>h('budgetNotes',e.target.value)} rows={3}
                placeholder="e.g. Budget was established based on original scope of work per contract..."
                className={inputCls}/>
            </div>

            <div className="flex justify-end">
              <button onClick={()=>setSection(2)}
                className="px-5 py-2 bg-[#1C1A18] text-white text-sm rounded-lg hover:bg-[#2E2C29]">
                Next: Suppliers →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — Suppliers */}
        {section===2 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-[#2E2C29]">Supplier Comparison <span className="text-[#9CA3AF] font-normal">(optional)</span></h2>
              <button onClick={()=>setForm(f=>({...f,suppliers:[...f.suppliers,EMPTY_SUPPLIER()]}))}
                className="text-xs text-[#1A7A4A] hover:underline">+ Add Supplier</button>
            </div>
            <p className="text-xs text-[#9CA3AF]">Leave empty if no supplier comparison needed — this slide will be skipped.</p>

            {form.suppliers.map((s,i)=>(
              <div key={i} className="border border-[#E5E7EB] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#6B7280]">Supplier {i+1}</span>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-[#6B7280] cursor-pointer">
                      <input type="checkbox" checked={s.selected}
                        onChange={e=>{
                          const sups=[...form.suppliers]
                          sups.forEach((_,j)=>sups[j]={...sups[j],selected:false})
                          sups[i]={...sups[i],selected:e.target.checked}
                          h('suppliers',sups)
                        }}/>
                      Selected Supplier
                    </label>
                    <button onClick={()=>{const sups=[...form.suppliers];sups.splice(i,1);h('suppliers',sups)}}
                      className="text-xs text-red-400 hover:text-red-600">Remove</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Supplier Name</label>
                    <input value={s.name} onChange={e=>{const sups=[...form.suppliers];sups[i]={...sups[i],name:e.target.value};h('suppliers',sups)}}
                      placeholder="Company name" className={inputCls}/>
                  </div>
                  <div>
                    <label className={labelCls}>Quotation Value (SAR)</label>
                    <input value={s.value} onChange={e=>{const sups=[...form.suppliers];sups[i]={...sups[i],value:e.target.value};h('suppliers',sups)}}
                      placeholder="0" className={inputCls}/>
                  </div>
                </div>
                {s.selected && (
                  <div>
                    <label className={labelCls}>Reason for Selection</label>
                    <input value={s.reason} onChange={e=>{const sups=[...form.suppliers];sups[i]={...sups[i],reason:e.target.value};h('suppliers',sups)}}
                      placeholder="e.g. Most competitive price among technically accepted suppliers"
                      className={inputCls}/>
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-between">
              <button onClick={()=>setSection(1)} className="px-5 py-2 border border-[#E5E7EB] text-[#6B7280] text-sm rounded-lg hover:bg-[#FAFAFA]">← Back</button>
              <button onClick={()=>setSection(3)} className="px-5 py-2 bg-[#1C1A18] text-white text-sm rounded-lg hover:bg-[#2E2C29]">Next: Variance →</button>
            </div>
          </div>
        )}

        {/* STEP 3 — Variance */}
        {section===3 && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4">
            <h2 className="text-sm font-bold text-[#2E2C29] mb-2">Variance Explanation <span className="text-[#9CA3AF] font-normal">(optional)</span></h2>
            <p className="text-xs text-[#9CA3AF]">Add reasons for the cost variance. Leave empty to skip this slide.</p>

            {form.varianceReasons.map((r,i)=>(
              <div key={i} className="flex gap-2 items-start">
                <span className="mt-2 w-5 h-5 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center flex-shrink-0">{i+1}</span>
                <input value={r} onChange={e=>{const rs=[...form.varianceReasons];rs[i]=e.target.value;h('varianceReasons',rs)}}
                  placeholder={`Reason ${i+1}...`} className={inputCls}/>
                <button onClick={()=>{const rs=[...form.varianceReasons];rs.splice(i,1);h('varianceReasons',rs)}}
                  className="mt-2 text-slate-300 hover:text-red-400 text-xs">✕</button>
              </div>
            ))}
            <button onClick={()=>h('varianceReasons',[...form.varianceReasons,''])}
              className="text-xs text-[#1A7A4A] hover:underline">+ Add Reason</button>

            <div>
              <label className={labelCls}>Conclusion (appears on last slide)</label>
              <textarea value={form.conclusion} onChange={e=>h('conclusion',e.target.value)} rows={3}
                placeholder="e.g. Based on the above analysis, the cost variance is justified and the selected supplier offers the best value..."
                className={inputCls}/>
            </div>

            <div className="flex justify-between">
              <button onClick={()=>setSection(2)} className="px-5 py-2 border border-[#E5E7EB] text-[#6B7280] text-sm rounded-lg hover:bg-[#FAFAFA]">← Back</button>
              <button onClick={()=>setSection(4)} className="px-5 py-2 bg-[#1C1A18] text-white text-sm rounded-lg hover:bg-[#2E2C29]">Preview →</button>
            </div>
          </div>
        )}

        {/* STEP 4 — PREVIEW */}
        {section===4 && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
              <h2 className="text-sm font-bold text-[#2E2C29] mb-5">Presentation Preview</h2>

              {/* Slide previews */}
              <div className="space-y-3">
                {/* Slide 1 */}
                <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                  <div className="bg-slate-800 px-4 py-2 text-xs text-[#9CA3AF] font-medium">Slide 1 — Title</div>
                  <div className="bg-[#0A2342] p-6 text-white">
                    <p className="text-xs tracking-widest text-teal-300 uppercase mb-1">Cost Assessment</p>
                    <p className="text-2xl font-bold">{form.packageName||'Package Name'}</p>
                    <p className="text-teal-400 text-sm mt-1">{form.projectName}</p>
                    <p className="text-[#6B7280] text-xs mt-3">{form.reportDate||'Date'} · Top Management Review</p>
                  </div>
                </div>

                {/* Slide 2 */}
                <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                  <div className="bg-slate-800 px-4 py-2 text-xs text-[#9CA3AF] font-medium">Slide 2 — Budget vs. Forecast</div>
                  <div className="bg-[#F5F1EA] p-4 grid grid-cols-3 gap-3">
                    <div className="bg-[#1A4A7A] rounded-lg p-3 text-white text-center">
                      <p className="text-xs text-blue-200">Budget</p>
                      <p className="font-bold text-sm mt-0.5">SAR {fmtN(budget)||'—'}</p>
                    </div>
                    <div className="bg-[#0D7377] rounded-lg p-3 text-white text-center">
                      <p className="text-xs text-teal-200">Forecast</p>
                      <p className="font-bold text-sm mt-0.5">SAR {fmtN(forecast)||'—'}</p>
                    </div>
                    <div className={`rounded-lg p-3 text-white text-center ${over?'bg-red-700':'bg-amber-600'}`}>
                      <p className="text-xs opacity-80">Variance</p>
                      <p className="font-bold text-sm mt-0.5">{over?'+':''}SAR {fmtN(variance)||'—'}</p>
                    </div>
                  </div>
                </div>

                {/* Suppliers preview */}
                {form.suppliers.filter(s=>s.name&&s.value).length>0 && (
                  <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                    <div className="bg-slate-800 px-4 py-2 text-xs text-[#9CA3AF] font-medium">Slide 3 — Supplier Comparison</div>
                    <div className="bg-[#F5F1EA] p-4">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-slate-800 text-white">
                          <th className="px-3 py-1.5 text-left">#</th>
                          <th className="px-3 py-1.5 text-left">Supplier</th>
                          <th className="px-3 py-1.5 text-right">Value (SAR)</th>
                          <th className="px-3 py-1.5 text-center">Status</th>
                        </tr></thead>
                        <tbody>{form.suppliers.filter(s=>s.name&&s.value).map((s,i)=>(
                          <tr key={i} className={s.selected?'bg-teal-50':'bg-white'}>
                            <td className="px-3 py-1.5">{i+1}</td>
                            <td className="px-3 py-1.5 font-medium">{s.name}</td>
                            <td className="px-3 py-1.5 text-right">{fmtN(num(s.value))}</td>
                            <td className="px-3 py-1.5 text-center text-teal-600">{s.selected?'✓ Selected':'—'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Variance reasons preview */}
                {form.varianceReasons.filter(r=>r.trim()).length>0 && (
                  <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                    <div className="bg-slate-800 px-4 py-2 text-xs text-[#9CA3AF] font-medium">Slide — Variance Explanation</div>
                    <div className="bg-[#F5F1EA] p-4 space-y-2">
                      {form.varianceReasons.filter(r=>r.trim()).map((r,i)=>(
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="w-4 h-4 rounded-sm bg-teal-600 flex-shrink-0 mt-0.5"/>
                          <span className="text-[#2E2C29]">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conclusion */}
                <div className="rounded-xl overflow-hidden border border-[#E5E7EB]">
                  <div className="bg-slate-800 px-4 py-2 text-xs text-[#9CA3AF] font-medium">Last Slide — Conclusion</div>
                  <div className="bg-[#0A2342] p-4 text-white text-sm">
                    {form.conclusion || <span className="text-[#6B7280]">No conclusion added yet...</span>}
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-6 flex gap-3 justify-end">
                <button onClick={()=>setSection(3)} className="px-5 py-2 border border-[#E5E7EB] text-[#6B7280] text-sm rounded-lg hover:bg-[#FAFAFA]">← Edit</button>
                <button onClick={handleGenerate}
                  className="px-6 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 flex items-center gap-2">
                  📑 Generate & Open Report
                </button>
              </div>
            </div>

            {/* Saved reports */}
            {saved.length > 0 && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
                <h3 className="text-sm font-bold text-[#2E2C29] mb-4">Generated This Session</h3>
                <div className="space-y-2">
                  {saved.map(s=>(
                    <div key={s.id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                      <div>
                        <p className="text-sm font-medium text-[#1C1A18]">{s.packageName}</p>
                        <p className="text-xs text-[#9CA3AF]">{s.projectName} · Budget: SAR {fmtN(num(s.budget))} → SAR {fmtN(num(s.forecast))}</p>
                      </div>
                      <button onClick={()=>{setForm(s);setSection(1)}}
                        className="text-xs text-[#1A7A4A] hover:underline">Edit & Re-generate</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-xl text-sm font-medium text-white flex items-center gap-2 ${toast.type==='error'?'bg-red-600':'bg-emerald-600'}`}>
          {toast.type==='error'?'✕':'✓'} {toast.msg}
        </div>
      )}
    </div>
  )
}
